/* Customer behavior tracker — pageviews, time-on-page, key actions.
   Batches events and flushes via sendBeacon so it never blocks the UI. */
import { API } from "@/api";
import { pixelPage, pixelTrack } from "@/components/PixelsLoader";

const KEY = "rg_bsid";
let queue = [];
let flushTimer = null;

function sid() {
  let v = sessionStorage.getItem(KEY);
  if (!v) {
    v = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)) + Date.now().toString(36);
    sessionStorage.setItem(KEY, v);
  }
  return v;
}

// Normalize a pathname into a stable page label for aggregation.
export function pageLabel(pathname) {
  if (pathname === "/" || pathname === "") return "home";
  const seg = pathname.split("/").filter(Boolean);
  const first = seg[0];
  const map = {
    product: "product", cart: "cart", checkout: "checkout", shop: "shop",
    d: "department", category: "category", search: "search", favorites: "favorites",
    profile: "profile", orders: "my_orders", track: "track_order", login: "login",
    ai: "ai_studio", occasions: "occasions", info: "info", admin: "admin",
    ops: "staff_ops", staff: "staff_ops", gift: "gift", invest: "invest",
  };
  return map[first] || first.slice(0, 30);
}

function flush(useBeacon = false) {
  if (!queue.length) return;
  const body = JSON.stringify({
    session_id: sid(),
    ad_code: localStorage.getItem("rg_ad_code") || "",
    events: queue.splice(0, 30),
  });
  const url = `${API}/track/behavior`;
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {}
}

function push(ev) {
  // Don't record admin/staff internal navigation as customer behavior.
  if (["admin", "staff_ops"].includes(ev.page)) return;
  queue.push(ev);
  if (queue.length >= 12) flush();
  else {
    clearTimeout(flushTimer);
    flushTimer = setTimeout(() => flush(), 8000);
  }
}

let current = null; // { page, path, t0 }

export function trackPageview(pathname) {
  const now = Date.now();
  if (current) {
    push({ type: "leave", page: current.page, path: current.path, duration_ms: now - current.t0 });
  }
  const page = pageLabel(pathname);
  current = { page, path: pathname, t0: now };
  push({ type: "pageview", page, path: pathname });
  try { pixelPage(pathname); } catch { /* pixels never break tracking */ }
}

export function trackAction(name, meta = {}) {
  push({ type: "action", name, page: current?.page || "", path: current?.path || "", meta });
  try { pixelTrack(name, meta); } catch { /* silent */ }
}

// Flush pending events (incl. final leave) when the tab hides/closes.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (current && !["admin", "staff_ops"].includes(current.page)) {
        queue.push({ type: "leave", page: current.page, path: current.path, duration_ms: Date.now() - current.t0 });
      }
      if (current) current = { ...current, t0: Date.now() };
      flush(true);
    }
  });
}
